import { createRef, useEffect, useState } from 'react';
import Form from '@rjsf/core';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import ImageRadioWidget from './CustomFIelds/RadioWidget';
import ColorSelectWidget from './CustomFIelds/ColorWidget';
import { useFormStore } from '../../state/FormStore';
import UnderConstruction from './CustomFIelds/UnderConstruction';
import { t } from '../../utils/i18n';

const formRef = createRef();

// Helper function to check if addon type exists in product
const hasAddonType = (productData, addonType) => {
    if (!productData || !productData.addons) return false;
    return productData.addons.some(addon => addon.type === addonType);
};

// Generate dynamic schema based on available addons
const generateDynamicSchema = (productData, formData = null) => {
    const vWidth = formData?.maat?.breedte || 0;
    const vDepth = formData?.maat?.uitval || 0;

    const getValidAddons = (addonType) => {
        if (!productData || !productData.addons) return { valid: [], exceeded: false };
        
        let exceeded = false;
        const addonsOfType = productData.addons.filter(addon => addon.type === addonType);
        
        const valid = addonsOfType.filter(addon => {
            if (!addon.pricing_data) return true;
            const maxW = Math.max(0, ...((addon.pricing_data.widths || []).map(Number).filter(n => !isNaN(n))));
            const maxH = Math.max(0, ...((addon.pricing_data.heights || addon.pricing_data.depths || []).map(Number).filter(n => !isNaN(n))));
            
            if (maxW > 0 && vWidth > maxW) {
                exceeded = true;
                return false;
            }
            if (maxH > 0 && vDepth > maxH) {
                exceeded = true;
                return false;
            }
            return true;
        });
        return { valid, exceeded };
    };

    const schema = {
        type: 'object',
        properties: {
            purchase_mode: {
                type: 'object',
                title: t('Purchase Mode'),
                properties: {
                    mode_option: {
                        type: 'string',
                        title: ' ',
                        default: "Complete Veranda",
                        enum: ['Complete Veranda', 'Standalone Doors Only'],
                        enumNames: [t('Complete Veranda'), t('Standalone Doors Only')],
                    },
                },
            },
            maat: {
                type: 'object',
                title: t('Maat'),
                properties: {
                    breedte: {
                        type: "number",
                        title: t("Breedte"),
                        default: 600,
                        minimum: 200,
                        maximum: 700
                    },
                    uitval: {
                        type: "number",
                        title: t("Uitval"),
                        default: 300,
                        minimum: 200,
                        maximum: 500
                    },
                    doorloop_hoogte: {
                        type: "number",
                        title: t("Doorloop Hoogte"),
                        default: 220,
                        minimum: 220,
                        maximum: 250
                    },
                },
            },
            uitval_type: {
                type: 'object',
                title: t('Uitval type'),
                properties: {
                    uitval_type_option: {
                        type: 'string',
                        title: ' ',
                        default: "Buiten staander",
                        enum: ['Buiten staander', 'Midden staander', 'Binnenkant staander', 'Buitenkant goot'],
                        enumNames: [t('Buiten staander'), t('Midden staander'), t('Binnenkant staander'), t('Buitenkant goot')],
                    },
                },
            },
        }
    };

    // Add colors dynamically if available
    if (productData && productData.colors && productData.colors.length > 0) {
        // colors are full objects: { id, name, hex }
        const colorEnums = productData.colors.map(c => c.name || c);
        schema.properties.kleur = {
            type: 'object',
            title: t('Kleur'),
            properties: {
                kleur_option: {
                    type: 'string',
                    title: ' ',
                    default: colorEnums[0] || 'Antraciet',
                    enum: colorEnums,
                    enumNames: colorEnums.map(c => t(c)),
                },
            },
        };
    } else {
        // Fallback to default colors
        schema.properties.kleur = {
            type: 'object',
            title: t('Kleur'),
            properties: {
                kleur_option: {
                    type: 'string',
                    title: ' ',
                    default: "Antraciet",
                    enum: ['Antraciet', 'Creme', 'Zwart'],
                    enumNames: [t('Antraciet'), t('Creme'), t('Zwart')],
                },
            },
        };
    }

    // Dak options - dynamically build from available roof addons
    const roofAddons = productData?.addons?.filter(addon =>
        addon.type === 'Glass roof' || addon.type === 'Polycarbonaat roof'
    ) || [];

    if (roofAddons.length > 0) {
        const dakEnums = roofAddons.map(addon => addon.name);
        const dakEnumNames = roofAddons.map(addon => t(addon.name));

        schema.properties.dak = {
            type: 'object',
            title: t('Dak'),
            properties: {
                dak_option: {
                    type: 'string',
                    title: ' ',
                    default: dakEnums[0],
                    enum: dakEnums,
                    enumNames: dakEnumNames,
                },
            },
        };
    }

    // Linker (Left side) - only show if side addons are available
    const hasAlumSides = hasAddonType(productData, 'Aluminium Door (Sides)');
    const hasGlassSides = hasAddonType(productData, 'Glass Door (Sides)');
    
    const spieInfo = getValidAddons('Spie');
    const hasSpie = spieInfo.valid.length > 0 || spieInfo.exceeded;
    
    const spieEnums = ['Geen', ...spieInfo.valid.map(a => a.name)];
    const spieEnumNames = [t('Geen'), ...spieInfo.valid.map(a => t(a.name))];
    const spieTitle = (spieInfo.valid.length === 0 && spieInfo.exceeded) ? t(' Spie (Size Limit Exceeded)') : t(' Spie');

    if (hasAlumSides || hasGlassSides || hasSpie) {
        schema.properties.linker = {
            type: 'object',
            title: t('Linker'),
            properties: {},
        };

        if (hasAlumSides || hasGlassSides) {
            const wandEnums = ['Geen'];
            const wandEnumNames = [t('Geen')];

            if (hasGlassSides) {
                wandEnums.push('Glazenschuifdeur');
                wandEnumNames.push(t('Glazenschuifdeur'));
            }
            if (hasAlumSides) {
                wandEnums.push('Aluminium Schuifdeur');
                wandEnumNames.push(t('Aluminium Schuifdeur'));
            }

            schema.properties.linker.properties.linker_wand_option = {
                type: 'string',
                title: t('Wand'),
                default: 'Geen',
                enum: wandEnums,
                enumNames: wandEnumNames,
            };
        }

        if (hasSpie) {
            schema.properties.linker.properties.linker_spie_option = {
                type: 'string',
                title: spieTitle,
                default: 'Geen',
                enum: spieEnums,
                enumNames: spieEnumNames,
            };
        }
    }

    // Rechter (Right side) - same logic as linker
    if (hasAlumSides || hasGlassSides || hasSpie) {
        schema.properties.rechter = {
            type: 'object',
            title: t('Rechter'),
            properties: {},
        };

        if (hasAlumSides || hasGlassSides) {
            const wandEnums = ['Geen'];
            const wandEnumNames = [t('Geen')];

            if (hasGlassSides) {
                wandEnums.push('Glazenschuifdeur');
                wandEnumNames.push(t('Glazenschuifdeur'));
            }
            if (hasAlumSides) {
                wandEnums.push('Aluminium Schuifdeur');
                wandEnumNames.push(t('Aluminium Schuifdeur'));
            }

            schema.properties.rechter.properties.rechter_wand_option = {
                type: 'string',
                title: t('Wand'),
                default: 'Geen',
                enum: wandEnums,
                enumNames: wandEnumNames,
            };
        }

        if (hasSpie) {
            schema.properties.rechter.properties.rechter_spie_option = {
                type: 'string',
                title: spieTitle,
                default: 'Geen',
                enum: spieEnums,
                enumNames: spieEnumNames,
            };
        }
    }

    // Zonwering - only show if available
    if (hasAddonType(productData, 'Zonwering')) {
        schema.properties.zonwering = {
            type: 'object',
            title: t('Zonwering'),
            properties: {
                zonwering_option: {
                    type: 'string',
                    title: ' ',
                    default: 'Nee',
                    enum: ['Nee', 'Ja'],
                    enumNames: [t('Nee'), t('Ja')],
                },
            },
        };
    }

    // Verlichting (LED) — show toggle + pattern picker if LED addons exist
    const ledInfo = getValidAddons('LED');
    const hasLED = ledInfo.valid.length > 0 || ledInfo.exceeded;
    const ledPatternEnums = ['Geen', ...ledInfo.valid.map(a => a.name)];
    const ledPatternNames = [t('Geen'), ...ledInfo.valid.map(a => t(a.name))];

    if (hasLED) {
        const ledEnabled = formData?.verlichting?.led_option === 'Ja';

        const verlichtingProperties = {
            led_option: {
                type: 'string',
                title: ' ',
                default: 'Nee',
                enum: ['Nee', 'Ja'],
                enumNames: [t('Nee'), t('Ja')],
            },
        };

        // Only show LED Patroon picker when LED is enabled
        if (ledEnabled) {
            verlichtingProperties.led_patroon_option = {
                type: 'string',
                title: t('LED Patroon'),
                default: 'Geen',
                enum: ledPatternEnums,
                enumNames: ledPatternNames,
            };
        }

        schema.properties.verlichting = {
            type: 'object',
            title: t('Verlichting'),
            properties: verlichtingProperties,
        };
    }

    // Gutter - only show if available
    if (hasAddonType(productData, 'Gutter')) {
        schema.properties.gutter = {
            type: 'object',
            title: t('Gutter'),
            properties: {
                goot_option: {
                    type: 'string',
                    title: t('Gutter'),
                    default: 'Rond',
                    enum: ['Klassiek', 'Vierkant', 'Rond'],
                    enumNames: [t('Klassiek'), t('Vierkant'), t('Rond')],
                },
            },
        };
    }

    // Voorkant (Front door) - only show if front door addons are available
    const hasAlumFront = hasAddonType(productData, 'Aluminium Door (Front)');
    const hasGlassFront = hasAddonType(productData, 'Sliding Wall (Front)');

    if (hasAlumFront || hasGlassFront) {
        const doorEnums = ['Geen'];
        const doorEnumNames = [t('Geen')];

        if (hasAlumFront) {
            doorEnums.push('Aluminium Schuifdeur');
            doorEnumNames.push(t('Aluminium Schuifdeur'));
        }
        if (hasGlassFront) {
            doorEnums.push('Glazen Schuifdeur');
            doorEnumNames.push(t('Glazen Schuifdeur'));
        }

        schema.properties.voorkant = {
            type: 'object',
            title: t('Door'),
            properties: {
                schuifdeur: {
                    type: 'string',
                    title: t('Door'),
                    default: 'Geen',
                    enum: doorEnums,
                    enumNames: doorEnumNames,
                },
            },
        };
    }

    return schema;
};




const widgets = {
    ImageRadioWidget: ImageRadioWidget,
    ColorSelectWidget: ColorSelectWidget,
    UnderConstruction: UnderConstruction,
};

const onError = (errors) => console.error('Form errors:', errors);

export const ConfigurationForm = ({ productData }) => {
    const { formData, setFormData } = useFormStore();
    const activeTab = useFormStore(state => state.activeTab);
    const [schema, setSchema] = useState(generateDynamicSchema(null));

    const isStandaloneDoors = formData?.purchase_mode?.mode_option === 'Standalone Doors Only';

    // Build uiSchema dynamically so we can inject colors into ColorSelectWidget
    const uiSchema = {
        'ui:classNames': 'darzo-input',
        purchase_mode: {
            mode_option: { 'ui:widget': 'ImageRadioWidget' }
        },
        uitval_type: {
            uitval_type_option: { 'ui:widget': 'ImageRadioWidget' }
        },
        kleur: {
            kleur_option: {
                'ui:widget': 'ColorSelectWidget',
                'ui:options': {
                    colors: productData?.colors || []
                }
            }
        },
        dak: {
            dak_option: { 'ui:widget': 'ImageRadioWidget' }
        },
        rechter: {
            rechter_wand_option: { 'ui:widget': 'ImageRadioWidget' },
            rechter_spie_option: { 'ui:widget': 'ImageRadioWidget' }
        },
        linker: {
            linker_wand_option: { 'ui:widget': 'ImageRadioWidget' },
            linker_spie_option: { 'ui:widget': 'ImageRadioWidget' }
        },
        zonwering: {
            zonwering_option: { 'ui:widget': 'ImageRadioWidget' }
        },
        verlichting: {
            led_option: { 'ui:widget': 'ImageRadioWidget' },
            led_patroon_option: { 'ui:widget': 'ImageRadioWidget' }
        },
        gutter: {
            goot_option: { 'ui:widget': 'ImageRadioWidget' }
        },
        voorkant: {
            schuifdeur: { 'ui:widget': 'ImageRadioWidget' }
        },
    };

    // Update schema when productData, size, or LED toggle changes
    useEffect(() => {
        if (productData) {
            const newSchema = generateDynamicSchema(productData, formData);
            setSchema(newSchema);
        }
    }, [productData, formData?.maat?.breedte, formData?.maat?.uitval, formData?.verlichting?.led_option]);

    const handleChange = ({ formData, errors }) => {
        if (errors && errors.length > 0) {
            console.error('Form errors:', errors);
        } else {
            const existingFormData = useFormStore.getState().formData;
            const updatedFormData = {
                ...existingFormData,
                ...formData
            };
            setFormData(updatedFormData);
        }
    };

    // Hide/show correct category fields based on activeTab and schema
    useEffect(() => {
        const rootFieldsetIds = [
            'purchase_mode', 'maat', 'uitval_type', 'kleur', 'dak', 
            'zonwering', 'verlichting', 'gutter', 'voorkant', 'linker', 'rechter'
        ];

        rootFieldsetIds.forEach(id => {
            const fieldset = document.querySelector(`.darzo-input #root_${id}`);
            if (fieldset) fieldset.style.display = 'none';
        });

        const activeFieldset = document.querySelector(`.darzo-input #root_${activeTab}`);
        if (activeFieldset) {
            const isMobile = window.innerWidth < 1000;
            activeFieldset.style.display = isMobile ? 'flex' : 'block';
        }
    }, [activeTab, schema]);

    // Hide Spie fields in standalone doors mode
    useEffect(() => {
        if (isStandaloneDoors) {
            const linkerSpieField = document.querySelector('.darzo-input #root_linker_linker_spie_option');
            const rechterSpieField = document.querySelector('.darzo-input #root_rechter_rechter_spie_option');

            if (linkerSpieField) linkerSpieField.style.display = 'none';
            if (rechterSpieField) rechterSpieField.style.display = 'none';
        } else {
            const linkerSpieField = document.querySelector('.darzo-input #root_linker_linker_spie_option');
            const rechterSpieField = document.querySelector('.darzo-input #root_rechter_rechter_spie_option');

            if (linkerSpieField) linkerSpieField.style.display = 'block';
            if (rechterSpieField) rechterSpieField.style.display = 'block';
        }
    }, [isStandaloneDoors, schema]);

    return (
        <div className={`active-tab-${activeTab}`}>
            <Form
                schema={schema}
                uiSchema={uiSchema}
                widgets={widgets}
                onChange={handleChange}
                onSubmit={(data) => console.log('Data submitted:', data)}
                validator={validator}
                onError={onError}
                ref={formRef}
                liveValidate={true}
            />
        </div>
    );
};
